import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Copy, Fuel, IndianRupee, Inbox, X } from "lucide-react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { API_BASE_URL } from "../../config/apiConfig";

export function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const origin = API_BASE_URL.replace(/\/api\/?$/i, "");
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function roleFlags(role) {
  const r = String(role || "").toLowerCase();
  return {
    role: r,
    canManage: r === "admin" || r === "hr",
    canView: r === "admin" || r === "hr" || r === "finance",
    isFinance: r === "finance",
  };
}

const BADGE_MAP = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
  cancelled: "cancelled",
  applied: "applied",
  closed: "closed",
  active: "active",
  inactive: "inactive",
  eligible: "eligible",
  reimbursed: "reimbursed",
};

export function StatusBadge({ status, label }) {
  const key = String(status || "").toLowerCase();
  const variant = BADGE_MAP[key] || "pending";
  return <span className={`ab-badge ab-badge--${variant}`}>{label || key || "—"}</span>;
}

export function SummaryCards({ items = [] }) {
  return (
    <div className="ab-cards" role="list">
      {items.map((item) => (
        <article
          key={item.label}
          className={`ab-card${item.accent ? " ab-card--accent" : ""}${item.warn ? " ab-card--warn" : ""}`}
          role="listitem"
        >
          <span className="ab-card__label">{item.label}</span>
          <div className="ab-card__value">{item.value}</div>
          {item.hint ? <p className="ab-card__hint">{item.hint}</p> : null}
        </article>
      ))}
    </div>
  );
}

export function FeatureHeader({
  icon: Icon,
  title,
  subtitle,
  breadcrumb,
  actions,
}) {
  return (
    <header className="ab-header">
      <div>
        {breadcrumb ? <div className="ab-breadcrumb">{breadcrumb}</div> : null}
        <div className="ab-header__title-row">
          {Icon ? (
            <span className="ab-header__icon" aria-hidden>
              <Icon size={20} strokeWidth={2} />
            </span>
          ) : null}
          <div>
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
        </div>
      </div>
      {actions ? <div className="ab-header__actions">{actions}</div> : null}
    </header>
  );
}

export function FeatureDisabledBanner({ featureName, managePath, enabled, canManage, onEnable }) {
  if (enabled) return null;
  return (
    <div className="ab-banner ab-banner--off" role="status">
      <AlertTriangle className="ab-banner__icon" size={18} strokeWidth={2} aria-hidden />
      <div>
        <strong>{featureName} is off for this organisation</strong>
        <p>
          History remains visible. New entries are blocked until the feature is enabled.
          {canManage && onEnable ? " Use the toggle below to turn it on." : null}
          {managePath ? (
            <>
              {" "}
              Org toggle also lives under{" "}
              <Link to={managePath}>Payroll Settings</Link>.
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action }) {
  return (
    <div className="ab-empty">
      <span className="ab-empty__icon" aria-hidden>
        <Icon size={22} strokeWidth={1.75} />
      </span>
      <strong>{title}</strong>
      {description ? <span>{description}</span> : null}
      {action || null}
    </div>
  );
}

export function Drawer({ open, title, subtitle, onClose, children, footer, wide }) {
  if (!open) return null;

  const node = (
    <>
      <button type="button" className="ab-drawer-backdrop" aria-label="Close drawer" onClick={onClose} />
      <aside
        className={`ab-drawer${wide ? " ab-drawer--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ab-drawer-title"
      >
        <div className="ab-drawer__head">
          <div>
            <h3 id="ab-drawer-title">{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="ab-btn ab-btn--ghost ab-btn--sm" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="ab-drawer__body">{children}</div>
        {footer ? <div className="ab-drawer__foot">{footer}</div> : null}
      </aside>
    </>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : null;
}

export function ConfirmShell({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger,
  loading,
  onConfirm,
  onCancel,
}) {
  return (
    <ConfirmDialog
      open={open}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      danger={danger}
      loading={loading}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

/** Normalize text for duplicate matching (bill numbers, notes). */
export function normKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function sameMoney(a, b) {
  const left = Number(a);
  const right = Number(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  return Math.abs(left - right) < 0.005;
}

export function sameDate(a, b) {
  return String(a || "").slice(0, 10) === String(b || "").slice(0, 10);
}

/** Inline success banner after create/update — auto-dismisses. */
export function SuccessFlash({ open, title, message, onDismiss }) {
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => onDismiss?.(), 7000);
    return () => clearTimeout(timer);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div className="ab-success-flash" role="status" aria-live="polite">
      <span className="ab-success-flash__icon" aria-hidden>
        <CheckCircle2 size={20} strokeWidth={2} />
      </span>
      <div className="ab-success-flash__body">
        <strong>{title}</strong>
        {message ? <p>{message}</p> : null}
      </div>
      <button
        type="button"
        className="ab-btn ab-btn--ghost ab-btn--sm"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/**
 * Duplicate entry dialog: shows the existing matching record and lets the user
 * cancel or explicitly allow a duplicate.
 */
export function DuplicateRecordDialog({
  open,
  title = "Possible duplicate found",
  message = "An existing record matches the same employee and details. Review it below before continuing.",
  recordTitle = "Existing record",
  fields = [],
  statusLabel,
  statusKey,
  loading = false,
  allowLabel = "Allow duplicate",
  cancelLabel = "Don't add",
  onAllow,
  onCancel,
}) {
  if (!open) return null;

  const node = (
    <div
      className="ab-dup-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ab-dup-title"
      onClick={() => !loading && onCancel?.()}
    >
      <div className="ab-dup-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="ab-dup-dialog__head">
          <span className="ab-dup-dialog__icon" aria-hidden>
            <Copy size={20} strokeWidth={1.75} />
          </span>
          <div>
            <h2 id="ab-dup-title">{title}</h2>
            <p>{message}</p>
          </div>
        </div>

        <div className="ab-dup-card">
          <div className="ab-dup-card__top">
            <strong>{recordTitle}</strong>
            {statusLabel ? <StatusBadge status={statusKey || statusLabel} label={statusLabel} /> : null}
          </div>
          <dl className="ab-dup-card__grid">
            {fields.map((field) => (
              <div key={field.label} className="ab-dup-card__field">
                <dt>{field.label}</dt>
                <dd>{field.value ?? "—"}</dd>
              </div>
            ))}
          </dl>
        </div>

        <p className="ab-dup-hint">
          Choose <strong>{cancelLabel}</strong> to keep the existing entry, or{" "}
          <strong>{allowLabel}</strong> if this is intentionally a second entry.
        </p>

        <div className="ab-dup-dialog__actions">
          <button
            type="button"
            className="ab-btn ab-btn--secondary"
            disabled={loading}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="ab-btn ab-btn--primary"
            disabled={loading}
            onClick={onAllow}
          >
            {loading ? "Saving…" : allowLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : null;
}

export function OrgToggle({ label, hint, checked, disabled, onChange }) {
  return (
    <div className="ab-toggle-row">
      <div>
        <span className="ab-toggle-row__label">{label}</span>
        {hint ? <span className="ab-toggle-row__hint">{hint}</span> : null}
      </div>
      <label className="ab-switch">
        <input
          type="checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        <span className="ab-switch__track" />
      </label>
    </div>
  );
}

export function PaginationBar({ total, limit, offset, onChange }) {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <div className="ab-pagination">
      <span>
        Showing {from}–{to} of {total}
      </span>
      <div className="ab-row-actions">
        <button
          type="button"
          className="ab-btn ab-btn--secondary ab-btn--sm"
          disabled={offset <= 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </button>
        <span>
          Page {page} / {pages}
        </span>
        <button
          type="button"
          className="ab-btn ab-btn--secondary ab-btn--sm"
          disabled={offset + limit >= total}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="ab-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={`ab-tab${active === tab.id ? " ab-tab--active" : ""}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export const FeatureIcons = { Fuel, IndianRupee };
